import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import sageIcon from '~/assets/public/sage-intacct-icon.svg';
import { BackLink, Icon, Page, Tooltip } from '~/components';
import { useApi, useIntegrations, useWorkspace } from '~/contexts';
import SageSettingsPage from './SageSettingsPage';
import { colors } from '~/styles';

const SageLogo = styled.img`
  width: 2.875rem;
  height: 2.875rem;
  max-width: unset;
  border-radius: 50%;
`;

const PageTitle = styled(Page.Title)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export default function SageSettingsArea() {
  const { sageConnectionId } = useParams();
  const { workspace } = useWorkspace();
  const { sage } = useIntegrations();
  const api = useApi();
  const [sageConnection, setSageConnection] = useState({ data: null, isReady: false });

  const fetchSageConnection = useCallback(async () => {
    if (!sageConnectionId) return;
    try {
      const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getStatus();
      setSageConnection({ data, isReady: true });
    } catch {
      setSageConnection({
        data: { message: "Something's not right. Try reconnecting to your Sage Intacct company." },
        isReady: true,
      });
    }
  }, [api, sageConnectionId, workspace.id]);

  useEffect(() => {
    fetchSageConnection();
  }, [fetchSageConnection]);

  if (!sage) return null;

  return (
    <Page>
      <Page.Header>
        <BackLink defaultPath={`/app/${workspace.key}/settings/integrations`} ignoreHistory />

        <Page.Info>
          <Page.Eyebrow>Settings</Page.Eyebrow>
          <PageTitle>
            {sageConnection.isReady ? (
              <>
                {sageConnection.data?.name ?? 'Sage Intacct'}
                {sageConnection.data?.status !== 'ok' && (
                  <Tooltip message={sageConnection.data?.message}>
                    <Icon icon="warning" color={colors.warning} />
                  </Tooltip>
                )}
              </>
            ) : (
              'Sage Intacct'
            )}
          </PageTitle>
        </Page.Info>

        <Page.Actions>
          <SageLogo src={sageIcon} />
        </Page.Actions>
      </Page.Header>

      <SageSettingsPage />
    </Page>
  );
}
